/* eslint-disable react/jsx-props-no-spreading */
import Cookies from 'js-cookie';
import { useEffect, useState, StrictMode } from 'react';
import { useLocation } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { AuthProvider as CognitoProvider } from 'react-oidc-context';

import { AuthProviders } from 'modules/auth/config/authConfig';
import { pwcSecurityConfig } from 'modules/auth/constants/configPWC';
import { pldSecurityConfig } from 'modules/auth/constants/configPLD';
import { defaultSecurityConfig } from 'modules/auth/constants/configBase';
import { applyAuth0PostLoginNavigation } from 'utils/auth0PostLoginNavigation';

const AUTH_PROVIDER =
  process.env.REACT_APP_AUTH_PROVIDER?.toLowerCase() || AuthProviders.AUTH0;

// Callback handler for Cognito sign-in (handles both SP and IdP-initiated SAML flows)
const onCognitoSigninCallback = () => {
  // Check if there's a stored return URL from IdP-initiated flow
  const returnUrl = sessionStorage.getItem('saml_return_url');
  if (returnUrl) {
    sessionStorage.removeItem('saml_return_url');
    // Use hash-based routing
    window.location.hash = returnUrl;
  }
};

const cognitoConfig = {
  authority: process.env.REACT_APP_AWS_COGNITO_AUTHORITY,
  client_id: process.env.REACT_APP_AWS_COGNITO_CLIENT_ID,
  redirect_uri: process.env.REACT_APP_AWS_COGNITO_REDIRECT_URI,
  scope: 'email openid phone profile',
  response_type: 'code',
  extraQueryParams: {
    lang: 'es',
  },
  onSigninCallback: onCognitoSigninCallback,
};

export const getTenantConfig = (tenant) => {
  const configs = {
    pwc: pwcSecurityConfig,
    pld: pldSecurityConfig,
    default: defaultSecurityConfig,
  };

  return configs[tenant] || configs.default;
};

const Auth0ProviderWithDynamicConfig = ({ children }) => {
  const location = useLocation();

  const [authConfig, setAuthConfig] = useState(() => {
    const savedTenant = Cookies.get('authTenant');
    return getTenantConfig(savedTenant);
  });

  useEffect(() => {
    if (AUTH_PROVIDER !== AuthProviders.AUTH0) return;

    const { hash, search } = location;

    // Don't change tenant config during Auth0 callback (when code parameter is present)
    // This prevents resetting the tenant when LegalRoute redirects to /login
    // before Auth0 SDK finishes processing the callback
    const urlParams = new URLSearchParams(search);
    const isAuth0Callback = urlParams.has('code') && urlParams.has('state');

    if (isAuth0Callback) {
      console.log('Auth0 callback detected, preserving current tenant config'); // eslint-disable-line
      return;
    }

    if (hash.includes('/pwc-login')) {
      console.log('PWC Space detected', window.location.origin); // eslint-disable-line

      Cookies.set('authTenant', 'pwc', {
        secure: true,
        sameSite: 'strict',
      });

      setAuthConfig(getTenantConfig('pwc'));
    } else if (hash.includes('/pld-login')) {
      console.log('PLD Space detected', window.location.origin); // eslint-disable-line

      Cookies.set('authTenant', 'pld', {
        secure: true,
        sameSite: 'strict',
      });

      setAuthConfig(getTenantConfig('pld'));
    } else if (hash.includes('/login')) {
      console.log('STTOK Space detected', window.location.origin); // eslint-disable-line

      Cookies.set('authTenant', 'default', {
        secure: true,
        sameSite: 'strict',
      });

      setAuthConfig(getTenantConfig('default'));
    }
  }, [location.hash, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRedirectCallback = (appState) => {
    if (AUTH_PROVIDER !== AuthProviders.AUTH0) return;

    const managedErrors = [
      'unauthorized',
      'access_denied',
      'invalid_certificate',
    ];

    if (appState && appState.error) {
      if (managedErrors.includes(appState.error)) {
        const tenant = Cookies.get('authTenant');
        const config = getTenantConfig(tenant);

        window.location.href =
          `https://${config.domain}/authorize?` +
          `response_type=code&` +
          `client_id=${config.clientId}&` +
          `redirect_uri=${encodeURIComponent(
            config.authorizationParams.redirect_uri
          )}&` +
          `connection=${config.authorizationParams.connection}&` +
          `certificate_type=hard&` +
          `prompt=login`;
      }
      return;
    }

    applyAuth0PostLoginNavigation(appState);
  };

  // If using Cognito, wrap children with OidcProvider
  if (AUTH_PROVIDER === AuthProviders.COGNITO) {
    return (
      <StrictMode>
        <CognitoProvider {...cognitoConfig}>{children}</CognitoProvider>
      </StrictMode>
    );
  }

  // If using Auth0, wrap children with Auth0Provider
  return (
    <Auth0Provider
      key={authConfig.domain}
      {...authConfig}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens
      useRefreshTokensFallback={false}
    >
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderWithDynamicConfig;
